import { ILocalization } from "adminPanel/preparedMaterials/IPreparedMaterials";
import IBankCard from "adminPanel/settings/bankCards/IBankCard";
import {
  EmptyStorePrinter,
  IStoreAutoComplete,
  IStorePrinter,
} from "adminPanel/stores/IStore";
import { TransactionTypes } from "models/common-props/TransactionTypes";
import { NIL } from "uuid";

export enum ConnectionState {
  Unknown,
  Successfull,
  Failed,
}
export interface IPosReturnOptions {
  makeReturns: boolean;
  makeExchange: boolean;
  makeReturnsWithinDays: boolean;
  returnWithinDays: number;
  returnFromSalesWithOffer: boolean;
  returnCash: boolean;
  returnForeignCurrencies: boolean;
  returnAllowExchangeWithLessValueFromSales: boolean;
}

export interface IPosOrdersOptions {
  dealWithPickupOrders: boolean;
  dealWithDeliveryOrders: boolean;
  ordersReturningReasonIsMandatory: boolean;
  ordersCancellingReasonIsMandatory: boolean;
  ordersNoSalesReasonIsMandatory: boolean;
}

export enum RoundDirection {
  Up,
  Down,
}

export interface IPosRoundOptions {
  useRound: boolean;
  roundDirection: RoundDirection;
  roundToNearest: number;
}

export interface IPosSettleOptions {
  settleWithCash: boolean;
  settleInDebt: boolean;
  settleUseCashButton: boolean;
  settleUseSettlementButton: boolean;
  settleCashDiscount: boolean;
}

export interface IPrintDesign {
  id?: string;
  name: string;
  language: string;
}

export interface IPosPrintDesign {
  id: string;
  posId: string;
  printDesign: IPrintDesign;
  storePrinter: IStorePrinter | null;
  transactionType: TransactionTypes;
  askBeforePrint: boolean;
  printDuplicateCopyLabel: boolean;
  enabled: boolean;
}
export interface IStorePrinterCustomizationDto {
  id: string;
  storePrinterId: string;
  printer?: string;
  paperSize?: string;
}
export interface ICurrency {
  id: string;
  code: string;
  name: string;
  name_Ar: string;
  flagCode: string;
  iso: string;
  isDefault: boolean;
  isActive: boolean;
  exchangeFactor: number;
}

export interface IPosUser {
  id: string;
  user: IUser;
  name: string;
  isActive: boolean;
  isSupervisor: boolean;
}

export interface IUser {
  id: string;
  name: string;
}

export interface IStoreDto {
  id: string;
  name: string;
  storePrinterCustomizations?: IStorePrinterCustomizationDto[];
  isStoreIsRestaurant?: boolean;
}

export interface StoreInfoDto {
  id: string;
  isStoreIsRestaurant?: boolean;
  useFastService?: boolean;
  useFullService?: boolean;
  isSalesReasonMandatory: boolean;
  usePreparation: boolean;
  useSendToKitchenCommand: boolean;
  allowAssignDriverBeforePreparationComplete: boolean;
  useServing: boolean;
  autoClearTableOnOrderCompletion: boolean;
  useExpectedPreparationTime: boolean;
  groupSimilarItemsInPosScreen?: boolean;
  groupSimilarItemsInKitchenAndCustomizationPrint?: boolean;
  groupSimilarItemsInCustomerReceiptPrint?: boolean;
  showLastOrderActivityOnTableMap?: boolean;
  idleAlertAfterMinutes?: number;
  storePrinterCustomizations?: IStorePrinterCustomizationDto[];
  posStationNumber?: number | null;
}

export const EmptyCallerID: ICallerID = {
  id: NIL,
  serviceURL: "",
  userName: "",
  password: "",
  uri: "",
};

export const EmptyStoreAutoComplete: IStoreAutoComplete = {
  id: NIL,
  name: "",
};

export const EmoptyPrintDesign = {
  id: "",
  name: "",
  language: "",
};

export const EmptyPosPrintDesignSale: IPosPrintDesign = {
  id: "",
  posId: "",
  printDesign: EmoptyPrintDesign,
  storePrinter: EmptyStorePrinter,
  transactionType: TransactionTypes.sales,
  askBeforePrint: true,
  printDuplicateCopyLabel: true,
  enabled: false,
};

export const EmptyPosPrintDesignSalesReturns: IPosPrintDesign = {
  id: "",
  posId: "",
  printDesign: EmoptyPrintDesign,
  storePrinter: EmptyStorePrinter,
  transactionType: TransactionTypes.salesReturns,
  askBeforePrint: true,
  printDuplicateCopyLabel: true,
  enabled: false,
};

export const EmptyPosPrintDesignTableOrders: IPosPrintDesign = {
  id: "",
  posId: "",
  printDesign: EmoptyPrintDesign,
  storePrinter: EmptyStorePrinter,
  transactionType: TransactionTypes.tableOrders,
  askBeforePrint: true,
  printDuplicateCopyLabel: true,
  enabled: false,
};

export const EmptyPosPrintDesignDeliveryOrder: IPosPrintDesign = {
  id: "",
  posId: "",
  printDesign: EmoptyPrintDesign,
  storePrinter: EmptyStorePrinter,
  transactionType: TransactionTypes.deliveryOrder,
  askBeforePrint: true,
  printDuplicateCopyLabel: true,
  enabled: false,
};

export const EmptyPosPrintDesignPickUpOrder: IPosPrintDesign = {
  id: "",
  posId: "",
  printDesign: EmoptyPrintDesign,
  storePrinter: EmptyStorePrinter,
  transactionType: TransactionTypes.pickUpOrder,
  askBeforePrint: true,
  printDuplicateCopyLabel: true,
  enabled: false,
};

export const EmptyPosPrintDesignShiftSummary: IPosPrintDesign = {
  id: "",
  posId: "",
  printDesign: EmoptyPrintDesign,
  storePrinter: EmptyStorePrinter,
  transactionType: TransactionTypes.shiftSummary,
  askBeforePrint: false,
  printDuplicateCopyLabel: true,
  enabled: false,
};
interface IStoreSectionPos {
  storeSectionId: string;
  name: string;
}

export const EmptyStoreSectionPos: IStoreSectionPos = {
  storeSectionId: "",
  name: "",
};
export interface ICallerID {
  id: string;
  serviceURL: string;
  userName: string;
  uri: string;
  password: string;
}
export default interface IPosProperties {
  id: string;
  localizations: ILocalization[];
  useReturnCoupons: boolean;
  useFullService: boolean;
  updatedOn: string;
  numberingPrefix: string;
  store: IStoreDto;
  storeInfo: StoreInfoDto;
  users: IPosUser[];
  currencies: ICurrency[];
  bankCards: IBankCard[];
  returnOptions: IPosReturnOptions;
  ordersOptions: IPosOrdersOptions;
  roundOptions: IPosRoundOptions;
  settleOptions: IPosSettleOptions;
  posPrintDesigns: IPosPrintDesign[];
  printingServiceUrl: string;
  storeId: string;
  storeSectionsPos: IStoreSectionPos[];
  callerID: ICallerID;
  isPosHasSupervisors: boolean;
}

export const EmptyStoreInfoDto: StoreInfoDto = {
  id: NIL,
  isStoreIsRestaurant: false,
  useFastService: false,
  useFullService: false,
  isSalesReasonMandatory: false,
  usePreparation: false,
  useSendToKitchenCommand: false,
  allowAssignDriverBeforePreparationComplete: false,
  useServing: false,
  autoClearTableOnOrderCompletion: false,
  useExpectedPreparationTime: false,
  groupSimilarItemsInPosScreen: true,
  groupSimilarItemsInKitchenAndCustomizationPrint: true,
  groupSimilarItemsInCustomerReceiptPrint: true,
  showLastOrderActivityOnTableMap: true,
  idleAlertAfterMinutes: 15,
  storePrinterCustomizations: [],
};

export const EmptyPosProperties: IPosProperties = {
  id: NIL,
  localizations: [],
  useReturnCoupons: false,
  useFullService: false,
  storeId: NIL,
  updatedOn: "",
  numberingPrefix: "",
  store: EmptyStoreAutoComplete,
  users: [],
  currencies: [],
  bankCards: [],
  returnOptions: {
    makeReturns: false,
    makeExchange: false,
    makeReturnsWithinDays: false,
    returnWithinDays: 0,
    returnFromSalesWithOffer: false,
    returnCash: false,
    returnForeignCurrencies: false,
    returnAllowExchangeWithLessValueFromSales: false,
  },
  ordersOptions: {
    dealWithPickupOrders: false,
    dealWithDeliveryOrders: false,
    ordersReturningReasonIsMandatory: false,
    ordersCancellingReasonIsMandatory: false,
    ordersNoSalesReasonIsMandatory: false,
  },
  roundOptions: {
    useRound: false,
    roundDirection: RoundDirection.Up,
    roundToNearest: 0,
  },
  settleOptions: {
    settleWithCash: true,
    settleInDebt: false,
    settleUseCashButton: false,
    settleUseSettlementButton: false,
    settleCashDiscount: false,
  },
  posPrintDesigns: [
    { ...EmptyPosPrintDesignSale },
    // { ...EmptyPosPrintDesignSalesReturns },
    { ...EmptyPosPrintDesignTableOrders },
    { ...EmptyPosPrintDesignDeliveryOrder },
    { ...EmptyPosPrintDesignPickUpOrder },
    { ...EmptyPosPrintDesignShiftSummary },
  ],
  printingServiceUrl: "",
  storeSectionsPos: [],
  storeInfo: EmptyStoreInfoDto,
  callerID: EmptyCallerID,
  isPosHasSupervisors: false,
};
